import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/', label: 'Calculator', activeKey: 'calculator' as const },
  { to: '/learn', label: 'Learn', activeKey: 'learn' as const },
  { to: '/faq', label: 'FAQ', activeKey: 'faq' as const },
  { to: '/contact', label: 'Contact', activeKey: 'contact' as const },
  { to: '/about', label: 'About', activeKey: 'about' as const },
];

export const Navigation: React.FC = () => {
  const location = useLocation();
  const path = location.pathname;
  const [menuOpen, setMenuOpen] = useState(false);

  const active = {
    calculator: path === '/',
    learn: path === '/learn' || path.startsWith('/learn/'),
    faq: path === '/faq',
    about: path === '/about',
    contact: path === '/contact',
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [path]);

  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  const navLink = (to: string, label: string, isActive: boolean) => (
    <Link
      to={to}
      className={`px-3 py-2 text-sm font-medium transition-colors relative group ${
        isActive ? 'text-gold-300' : 'text-navy-100 hover:text-gold-300'
      }`}
    >
      {label}
      <span
        className={`absolute bottom-0 left-3 right-3 h-0.5 bg-gold-500 transition-transform origin-left ${
          isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
        }`}
      />
    </Link>
  );

  const drawerNavLink = (to: string, label: string, isActive: boolean) => (
    <Link
      to={to}
      onClick={() => setMenuOpen(false)}
      className={`block px-4 py-3 text-base font-medium border-b border-navy-800 transition-colors ${
        isActive ? 'text-gold-300 bg-navy-800/50' : 'text-navy-100 hover:text-gold-300 hover:bg-navy-800/30'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header id="app-nav" className="bg-navy-900 sticky top-0 z-50 shadow-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center gap-2">
          <Link
            to="/"
            aria-label="Mortgage Compare Calculator home"
            className="flex items-center gap-2.5 hover:opacity-90 transition-opacity shrink-0"
          >
            <div className="w-8 h-8 rounded-lg bg-gold-500 flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="10" width="3" height="6" rx="0.5" fill="#0F1F38"/>
                <rect x="7.5" y="6" width="3" height="10" rx="0.5" fill="#0F1F38"/>
                <rect x="13" y="2" width="3" height="14" rx="0.5" fill="#0F1F38"/>
              </svg>
            </div>
            <span className="font-serif text-lg text-white hidden sm:block tracking-tight">
              Mortgage<span className="text-gold-400">Compare</span>
            </span>
          </Link>

          <nav
            className="hidden md:flex items-center flex-nowrap justify-end gap-x-0.5"
            aria-label="Main"
          >
            {NAV_ITEMS.map(({ to, label, activeKey }) =>
              navLink(to, label, active[activeKey])
            )}
          </nav>

          <button
            type="button"
            className="md:hidden p-2 -mr-2 text-navy-100 hover:text-gold-300 rounded-lg transition-colors"
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-drawer"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" aria-hidden />
          </button>
        </div>
      </div>

      {menuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 bg-navy-950/60 z-[60] md:hidden animate-fade-in"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          />
          <div
            id="mobile-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            className="fixed top-0 right-0 h-full w-[min(100%,280px)] bg-navy-900 shadow-panel z-[70] md:hidden flex flex-col animate-slide-in-right"
          >
            <div className="flex items-center justify-end h-16 px-4 border-b border-navy-800 shrink-0">
              <button
                type="button"
                className="p-2 -mr-2 text-navy-100 hover:text-gold-300 rounded-lg transition-colors"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-6 h-6" aria-hidden />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2" aria-label="Main">
              {NAV_ITEMS.map(({ to, label, activeKey }) =>
                drawerNavLink(to, label, active[activeKey])
              )}
            </nav>
          </div>
        </>
      )}
    </header>
  );
};
